import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Clock, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import HCaptchaWidget from "../components/HCaptchaWidget";

/**
 * Iter59.p19 + Iter75 — Glass-Hero login with MFA + rate-limit awareness.
 *
 * States:
 *   • password    → email + password (+ hCaptcha widget after 3 fails)
 *   • mfa         → 6-digit TOTP challenge OR backup code
 *   • rotate      → user routed to /change-password because their
 *                    password rotation window (90 days) expired
 *   • lockedOut   → too-many-fails banner with retry-after timer
 */
export default function Login() {
    const { user, login, verifyMfa } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [step, setStep] = useState("password"); // password | mfa
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Iter75 — CAPTCHA state.
    const [captchaConfig, setCaptchaConfig] = useState({ site_key: "", enabled: false, trigger_after_failures: 3 });
    const [failCount, setFailCount] = useState(0);
    const [needsCaptcha, setNeedsCaptcha] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");

    // Iter75 — MFA challenge state.
    const [mfaChallenge, setMfaChallenge] = useState(null); // { challengeToken, email }
    const [mfaCode, setMfaCode] = useState("");
    const [useBackup, setUseBackup] = useState(false);
    // Iter81 — Trust-this-device opt-in on the MFA challenge screen.
    // Unchecked by default; user must explicitly opt in.
    const [trustDevice, setTrustDevice] = useState(false);

    // Iter75 — Session-timeout / absolute-limit banner reasons.
    const reason = params.get("reason");
    const inactiveBanner = reason === "inactive";
    const absoluteBanner = reason === "absolute";
    const rotatedBanner = reason === "password_updated";

    useEffect(() => {
        // Fetch captcha config lazily — one call per app boot.
        api.get("/auth/captcha-config").then(({ data }) => setCaptchaConfig(data)).catch(() => {});
    }, []);

    const postLoginTarget = () => "/";

    const resetToStart = useCallback(() => {
        setStep("password");
        setMfaChallenge(null);
        setMfaCode("");
        setUseBackup(false);
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const extra = needsCaptcha && captchaToken ? { captcha_token: captchaToken } : {};
            const result = await login(email.trim(), password, extra);
            if (result?.mfaRequired) {
                setMfaChallenge({ challengeToken: result.challengeToken, email: result.email });
                setStep("mfa");
                setFailCount(0);
                setNeedsCaptcha(false);
                return;
            }
            navigate(postLoginTarget(), { replace: true });
        } catch (err) {
            if (err.passwordRotationRequired) {
                // Direct to change-password with a soft-prompt banner.
                navigate("/change-password?reason=rotation", { replace: true });
                return;
            }
            setError(err.message || "Login failed");
            setFailCount((c) => c + 1);
            if (err.captchaRequired) setNeedsCaptcha(true);
            setCaptchaToken("");
        } finally {
            setSubmitting(false);
        }
    };

    const onMfaSubmit = async (e) => {
        e.preventDefault();
        if (!mfaChallenge) return;
        const codeVal = mfaCode.trim();
        if (!codeVal) return;
        setError("");
        setSubmitting(true);
        try {
            await verifyMfa(mfaChallenge.challengeToken, codeVal, useBackup, trustDevice);
            navigate(postLoginTarget(), { replace: true });
        } catch (err) {
            setError(err.message || "Verification failed");
        } finally {
            setSubmitting(false);
        }
    };

    // Trigger captcha on the NEXT attempt when the local fail count crosses the threshold.
    useEffect(() => {
        if (failCount >= (captchaConfig.trigger_after_failures || 3)) {
            setNeedsCaptcha(true);
        }
    }, [failCount, captchaConfig.trigger_after_failures]);

    if (user && user !== false) return <Navigate to={postLoginTarget()} replace />;

    return (
        <div
            className="asl-login-hero"
            data-testid="login-page"
            style={{
                position: "fixed",
                inset: 0,
                overflow: "hidden",
                backgroundColor: "#0F1B3D",
            }}
        >
            <img
                src="/hero/login-hero.jpg"
                alt=""
                aria-hidden="true"
                data-testid="login-hero-image"
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "30% center",
                    zIndex: 0,
                }}
            />
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background:
                        "linear-gradient(135deg, rgba(15,27,61,0.75) 0%, rgba(27,42,86,0.55) 50%, rgba(15,27,61,0.35) 100%)",
                }}
            />

            <div className="asl-login-card-wrap">
                {step === "password" ? (
                    <PasswordCard
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        onSubmit={onSubmit}
                        submitting={submitting}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                        error={error}
                        needsCaptcha={needsCaptcha}
                        captchaConfig={captchaConfig}
                        captchaToken={captchaToken}
                        setCaptchaToken={setCaptchaToken}
                        inactiveBanner={inactiveBanner}
                        absoluteBanner={absoluteBanner}
                        rotatedBanner={rotatedBanner}
                    />
                ) : (
                    <MfaCard
                        email={mfaChallenge?.email || email}
                        code={mfaCode}
                        setCode={setMfaCode}
                        useBackup={useBackup}
                        setUseBackup={setUseBackup}
                        trustDevice={trustDevice}
                        setTrustDevice={setTrustDevice}
                        onSubmit={onMfaSubmit}
                        submitting={submitting}
                        error={error}
                        onCancel={resetToStart}
                    />
                )}
            </div>

            <div
                className="asl-login-badges"
                aria-hidden="true"
                data-testid="login-module-badges"
            >
                {["IC", "AP", "CN"].map((code) => (
                    <span
                        key={code}
                        style={{
                            background: "rgba(255,255,255,0.2)",
                            color: "#FFFFFF",
                            border: "1px solid rgba(255,255,255,0.25)",
                            padding: "4px 12px",
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.05em",
                        }}
                    >
                        {code}
                    </span>
                ))}
            </div>

            <div
                className="asl-login-copyright"
                data-testid="login-copyright"
                style={{
                    color: "rgba(255,255,255,0.4)",
                    fontSize: 10,
                }}
            >
                © {new Date().getFullYear()} Kaafi Hire Employment Services Ltd
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Password card (default step).
// ---------------------------------------------------------------------------
function PasswordCard({
    email, setEmail, password, setPassword, onSubmit, submitting, showPassword,
    setShowPassword, error, needsCaptcha, captchaConfig, captchaToken, setCaptchaToken,
    inactiveBanner, absoluteBanner, rotatedBanner,
}) {
    return (
        <form onSubmit={onSubmit} className="asl-glass-card" data-testid="login-form">
            <div className="flex flex-col items-center mb-6">
                <img
                    src="/asl-logo.png"
                    alt="ASL"
                    data-testid="login-logo"
                    style={{ height: 48, width: "auto" }}
                    className="mb-4 asl-logo--washed-strong"
                />
                <h1
                    className="text-white font-bold tracking-tight text-center"
                    style={{ fontSize: 24, lineHeight: 1.25 }}
                >
                    ASL HR Management Platform
                </h1>
                <p
                    className="text-center mt-1"
                    style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}
                    data-testid="login-subtitle"
                >
                    Managed by Kaafi Hire Employment Services Ltd
                </p>
            </div>

            {inactiveBanner && (
                <BannerBar icon={<Clock className="h-4 w-4 mt-0.5 shrink-0" />}
                    title="You were signed out due to inactivity."
                    subtitle="Please log in again to continue."
                    testid="login-inactive-banner"
                />
            )}
            {absoluteBanner && (
                <BannerBar icon={<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
                    title="Your session reached the 8-hour maximum."
                    subtitle="Please sign in again to continue."
                    testid="login-absolute-banner"
                />
            )}
            {rotatedBanner && (
                <BannerBar icon={<ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />}
                    title="Password updated."
                    subtitle="Sign in with your new password."
                    testid="login-rotated-banner"
                    tone="ok"
                />
            )}

            <div className="space-y-4">
                <div>
                    <label
                        htmlFor="email"
                        className="block mb-1.5 font-medium"
                        style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}
                    >
                        Email
                    </label>
                    <input
                        id="email"
                        type="email"
                        autoComplete="username"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                        className="asl-glass-input"
                    />
                </div>
                <div>
                    <label
                        htmlFor="password"
                        className="block mb-1.5 font-medium"
                        style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}
                    >
                        Password
                    </label>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="asl-glass-input pr-10"
                            data-testid="login-password-input"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                            className="absolute inset-y-0 right-0 flex items-center px-3 focus:outline-none"
                            style={{ color: "rgba(255,255,255,0.65)" }}
                            tabIndex={0}
                            data-testid="login-password-toggle"
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4" aria-hidden="true" />
                            ) : (
                                <Eye className="h-4 w-4" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {needsCaptcha && (
                <div className="mt-4" data-testid="login-captcha-slot">
                    <HCaptchaWidget siteKey={captchaConfig.site_key} onVerify={setCaptchaToken} />
                </div>
            )}

            {error && (
                <div
                    className="mt-4 text-sm rounded-full px-4 py-2 text-center"
                    style={{
                        background: "rgba(168,50,74,0.35)",
                        color: "#FFE0E6",
                        border: "1px solid rgba(255,150,170,0.4)",
                    }}
                    data-testid="login-error"
                >
                    {error}
                </div>
            )}

            <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-button"
                className="w-full mt-5 rounded-lg font-semibold transition"
                style={{
                    background: "#1B2A56",
                    color: "#FFFFFF",
                    height: 44,
                    border: "1px solid rgba(255,255,255,0.15)",
                    opacity: submitting ? 0.75 : 1,
                    cursor: submitting ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = "#2A3B6B"; }}
                onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = "#1B2A56"; }}
            >
                {submitting ? "Signing in…" : "Sign In"}
            </button>

            <p
                className="text-center mt-4"
                style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}
                data-testid="login-trust-line"
            >
                Trusted by 3 divisions · 180+ employees
            </p>

            {/* Iter79/Iter80.1 — Break-glass emergency recovery link (inside card) */}
            <p
                className="text-center"
                style={{ marginTop: 8, fontSize: 11 }}
            >
                <a
                    href="/auth/emergency-recovery"
                    className="no-underline hover:underline underline-offset-2"
                    style={{ color: "rgba(255,255,255,0.45)" }}
                    data-testid="login-emergency-recovery-link"
                >
                    Lost your phone and backup codes? Emergency recovery →
                </a>
            </p>
        </form>
    );
}

function BannerBar({ icon, title, subtitle, testid, tone = "warn" }) {
    const palette = tone === "ok"
        ? { bg: "rgba(46,160,90,0.20)", border: "rgba(180,255,205,0.45)", fg: "#DFFFE8" }
        : { bg: "rgba(168,50,74,0.18)", border: "rgba(255,180,190,0.45)", fg: "#FFDDE5" };
    return (
        <div
            className="rounded-md border px-3 py-2.5 text-sm flex items-start gap-2 mb-4"
            style={{ background: palette.bg, borderColor: palette.border, color: palette.fg }}
            data-testid={testid}
        >
            {icon}
            <div>
                <div className="font-semibold">{title}</div>
                {subtitle && <div className="text-xs opacity-80">{subtitle}</div>}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// MFA challenge card.
// ---------------------------------------------------------------------------
function MfaCard({ email, code, setCode, useBackup, setUseBackup, trustDevice, setTrustDevice, onSubmit, submitting, error, onCancel }) {
    return (
        <form onSubmit={onSubmit} className="asl-glass-card" data-testid="login-mfa-form">
            <div className="flex flex-col items-center mb-4">
                <ShieldCheck className="h-10 w-10 mb-2" style={{ color: "#FFFFFF" }} />
                <h1
                    className="text-white font-bold tracking-tight text-center"
                    style={{ fontSize: 22, lineHeight: 1.25 }}
                >
                    Two-factor authentication
                </h1>
                <p
                    className="text-center mt-1"
                    style={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}
                    data-testid="login-mfa-subtitle"
                >
                    {useBackup
                        ? "Enter one of your backup codes."
                        : `Enter the 6-digit code from your authenticator app for ${email}.`}
                </p>
            </div>

            <div>
                <label
                    htmlFor="mfa-code"
                    className="block mb-1.5 font-medium"
                    style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}
                >
                    {useBackup ? "Backup code" : "6-digit code"}
                </label>
                <input
                    id="mfa-code"
                    autoComplete="one-time-code"
                    inputMode={useBackup ? "text" : "numeric"}
                    autoFocus
                    placeholder={useBackup ? "ABCD-EFGH-JKLM-NPQR" : "000000"}
                    value={code}
                    onChange={(e) => {
                        const v = e.target.value;
                        setCode(useBackup ? v.toUpperCase() : v.replace(/\D/g, "").slice(0, 6));
                    }}
                    maxLength={useBackup ? 24 : 6}
                    required
                    className="asl-glass-input text-center font-mono"
                    style={{ letterSpacing: useBackup ? "0.1em" : "0.4em", fontSize: 18 }}
                    data-testid="login-mfa-code-input"
                />
            </div>

            {error && (
                <div
                    className="mt-4 text-sm rounded-full px-4 py-2 text-center"
                    style={{
                        background: "rgba(168,50,74,0.35)",
                        color: "#FFE0E6",
                        border: "1px solid rgba(255,150,170,0.4)",
                    }}
                    data-testid="login-mfa-error"
                >
                    {error}
                </div>
            )}

            {/* Iter81 — Trust-this-device opt-in. Unchecked by default. */}
            <label
                className="flex items-center gap-2 mt-4 cursor-pointer select-none"
                style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}
                data-testid="login-mfa-trust-device-label"
            >
                <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    data-testid="login-mfa-trust-device-checkbox"
                    style={{
                        accentColor: "#A8324A",
                        width: 15, height: 15, cursor: "pointer",
                    }}
                />
                <span>Trust this device for 7 days</span>
            </label>
            <p
                className="mt-1"
                style={{ color: "rgba(255,255,255,0.5)", fontSize: 10.5, marginLeft: 22 }}
                data-testid="login-mfa-trust-device-hint"
            >
                Skip the code on this browser until {new Date(Date.now() + 7 * 24 * 3600 * 1000).toLocaleDateString()}.
                Any password change, admin action, or suspicious login clears the trust.
            </p>

            <button
                type="submit"
                disabled={submitting || !code}
                data-testid="login-mfa-submit-button"
                className="w-full mt-5 rounded-lg font-semibold transition"
                style={{
                    background: "#1B2A56",
                    color: "#FFFFFF",
                    height: 44,
                    border: "1px solid rgba(255,255,255,0.15)",
                    opacity: submitting || !code ? 0.75 : 1,
                    cursor: submitting || !code ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = "#2A3B6B"; }}
                onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = "#1B2A56"; }}
            >
                {submitting ? "Verifying…" : "Verify"}
            </button>

            <div className="flex items-center justify-between mt-4 text-xs">
                <button
                    type="button"
                    onClick={() => { setUseBackup((v) => !v); setCode(""); }}
                    className="underline-offset-2 hover:underline flex items-center gap-1"
                    style={{ color: "rgba(255,255,255,0.75)" }}
                    data-testid="login-mfa-toggle-backup"
                >
                    <KeyRound className="h-3.5 w-3.5" />
                    {useBackup ? "Use authenticator code" : "Use a backup code"}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="hover:underline"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                    data-testid="login-mfa-back-button"
                >
                    Back
                </button>
            </div>

            {/* Iter79/Iter80.1 — Break-glass emergency recovery link (inside card) */}
            <p
                className="text-center"
                style={{ marginTop: 16, fontSize: 11 }}
            >
                <a
                    href="/auth/emergency-recovery"
                    className="no-underline hover:underline underline-offset-2"
                    style={{ color: "rgba(255,255,255,0.45)" }}
                    data-testid="login-emergency-recovery-link-mfa"
                >
                    Lost your phone and backup codes? Emergency recovery →
                </a>
            </p>
        </form>
    );
}
